import * as Icons from "lucide-react";
import { ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Countdown } from "@/components/ui/Countdown";
import { SlotsMeter } from "@/components/ui/SlotsMeter";
import { Reveal } from "@/components/ui/Reveal";

function timeAgo(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return null;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

export const WhyNow = ({ reasons = [], availability }) => {
  const latest = timeAgo(availability?.latest_issued_at);
  const open = availability?.is_open;

  return (
    <section id="why-now" className="gm-section" data-testid="why-now-section">
      <div className="gm-container grid gap-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-5">
          <SectionHeader
            eyebrow="Why now"
            title="Kenapa jangan tunggu lama"
            subtitle="The free window is funded by our broker partnership, so it is not permanent. Here is exactly what changes after it closes."
            testId="why-now-header"
          />

          {availability?.deadline && open ? (
            <div
              className="mt-7 rounded-xl p-5"
              data-testid="why-now-countdown-card"
              style={{
                background: "rgba(224,31,38,0.08)",
                border: "1px solid rgba(224,31,38,0.42)",
              }}
            >
              <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white">
                <Clock className="h-4 w-4" style={{ color: "#FF8A8E" }} aria-hidden="true" />
                Free access closes in
              </p>
              <div className="mt-3">
                <Countdown
                  target={availability.deadline}
                  label="Free access"
                  testId="why-now-countdown"
                />
              </div>
              <div className="mt-4">
                <SlotsMeter availability={availability} testId="why-now-slots" />
              </div>
              {latest ? (
                <p
                  className="mt-4 inline-flex items-center gap-2 text-[11px]"
                  data-testid="why-now-latest"
                  style={{ color: "var(--gm-muted-2)" }}
                >
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: "var(--gm-success)" }}
                  />
                  Latest free licence issued {latest}
                </p>
              ) : null}

              <Link
                to="/free-access"
                data-testid="why-now-cta"
                className="gm-btn-red mt-5 flex min-h-[48px] items-center justify-center gap-2 rounded-xl text-sm font-bold"
              >
                Claim your free licence
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : null}
        </div>

        <div className="lg:col-span-7">
          <div className="grid gap-4 sm:grid-cols-2">
            {reasons.map((r, i) => {
              const Icon = Icons[r.icon] || Icons.Sparkles;
              return (
                <Reveal key={r.slug} delay={i * 65}>
                  <article
                    className="gm-card gm-card-hover h-full"
                    data-testid={`why-now-card-${r.slug}`}
                  >
                    <span
                      className="inline-flex h-11 w-11 items-center justify-center rounded-xl"
                      style={{
                        background: "rgba(246,196,83,0.1)",
                        border: "1px solid rgba(246,196,83,0.3)",
                      }}
                    >
                      <Icon className="h-5 w-5" style={{ color: "var(--gm-warning)" }} />
                    </span>
                    <h3 className="mt-4 text-base font-bold text-white">{r.title}</h3>
                    <p
                      className="mt-2 text-sm leading-relaxed"
                      style={{ color: "var(--gm-muted)" }}
                    >
                      {r.body}
                    </p>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <p
            className="mt-5 rounded-xl p-4 text-xs leading-relaxed"
            data-testid="why-now-honesty-note"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "var(--gm-muted-2)",
            }}
          >
            No fake timers here. The countdown points at one real date and the slot
            counter moves only when an actual application comes in. Kalau the deadline
            gets extended, we update it on this page - we tak reset it every time you
            refresh.
          </p>
        </div>
      </div>
    </section>
  );
};
